<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  CreateIcon,
  IconViewCreateMutation,
  IconViewCreateMutationVariables,
  IconViewDataQuery,
  IconViewDataQueryVariables,
  IconViewDeleteMutation,
  IconViewDeleteMutationVariables,
  IconViewUpdateMutation,
  IconViewUpdateMutationVariables,
  UpdateIcon,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import TextField from '@/components/input/TextField.vue'
import ImagePicker from '@/components/input/ImagePicker.vue'
import { normalName, required } from '@/validation'
import ItemEditIcon from '@/components/button/ButtonEdit.vue'
import ItemDeleteIcon from '@/components/button/ButtonDelete.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'

type LocalIcon = IconViewDataQuery['product']['icons'][0]
const { t } = useI18n()

const fetchQuery = useQuery<IconViewDataQuery, IconViewDataQueryVariables>(gql`
  query IconViewData {
    product {
      icons {
        id
        name
        imageUpload {
          id
          imageUrl(options: { width: 48, height: 48 })
        }
      }
    }
  }
`)
const items = computed(() => fetchQuery.result.value?.product.icons || [])

type CreateModel = Partial<CreateIcon>
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>(
  { name: { required, normalName }, imageUploadId: { required } },
  createModel,
)
const createMutation = useMutation<IconViewCreateMutation, IconViewCreateMutationVariables>(gql`
  mutation IconViewCreate($command: CreateIcon!) {
    product {
      createIcon(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  createModel.name = undefined
  createModel.imageUploadId = undefined
  await createDialog.value?.open(() =>
    createMutation.mutate({ command: createModel as CreateIcon }).then(() => fetchQuery.refetch()),
  )
}

type UpdateModel = Partial<UpdateIcon>
const updateModel = reactive<UpdateModel>({})
const updateValidation = useVuelidate<UpdateModel>({ name: { required, normalName } }, updateModel)
const updateMutation = useMutation<IconViewUpdateMutation, IconViewUpdateMutationVariables>(gql`
  mutation IconViewUpdate($command: UpdateIcon!) {
    product {
      updateIcon(command: $command) {
        id
        name
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalIcon) => {
  updateModel.id = item.id
  updateModel.name = item.name
  await updateDialog.value?.open(() =>
    updateMutation.mutate({ command: updateModel as UpdateIcon }),
  )
}

const deleteMutation = useMutation<IconViewDeleteMutation, IconViewDeleteMutationVariables>(gql`
  mutation IconViewDelete($id: ID!) {
    product {
      deleteIcon(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()
const doDelete = async (item: LocalIcon) => {
  await deleteDialog.value?.open(item.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

const search = ref('')
const headers = [
  {
    key: 'imageUpload',
    title: t('entity.icon.field.image'),
    sortable: false,
    width: 100,
  },
  {
    key: 'name',
    title: t('entity.icon.field.name'),
  },
  {
    key: 'actions',
    width: 100,
    sortable: false,
    align: 'end',
  },
]
const sort = [{ key: 'name', order: 'asc' }]
</script>

<template>
  <h1>{{ t('entity.icon.plural') }}</h1>

  <data-table
    :items="items"
    :headers="headers"
    :loading="fetchQuery.loading.value"
    :sort-by="sort"
    :search="search"
    density="comfortable"
  >
    <template #header.actions>
      <data-table-search v-model="search" />
      <data-table-create-button @click="doCreate" />
    </template>
    <template #item.imageUpload="{ item }: { item: LocalIcon }">
      <v-img :src="item.imageUpload?.imageUrl" :alt="item.name" width="40" height="40" />
    </template>
    <template #item.actions="{ item }: { item: LocalIcon }">
      <item-edit-icon @click="doUpdate(item)" />
      <item-delete-icon @click="doDelete(item)" />
    </template>
  </data-table>

  <async-delete-dialog ref="deleteDialog" :warning="t('entity.icon.deleteWarning')" />

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.icon.singular')"
    :validation="createValidation"
  >
    <text-field
      v-model="createModel.name"
      :label="t('entity.icon.field.name')"
      required
      :validation="createValidation.name"
    />
    <image-picker
      v-model="createModel.imageUploadId"
      :label="t('entity.icon.field.image')"
      required
      :validation="createValidation.imageUploadId"
    />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.icon.singular')"
    :validation="updateValidation"
  >
    <text-field
      v-model="updateModel.name"
      required
      :label="t('entity.icon.field.name')"
      :validation="updateValidation.name"
    />
  </upsert-dialog>
</template>
